import { Injectable, signal } from '@angular/core';

const STORAGE_KEY = 'et-theme';

/**
 * Alternância de tema claro/escuro. O tema Front tem dois bundles standalone
 * (`theme.bundle.css` / `theme-dark.bundle.css`); alternamos qual `<link>` fica
 * ativo e marcamos a classe `dark-mode` no <html> (usada pelos overrides do
 * styles.scss e lida pelos gráficos via o signal `dark`).
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  /** Estado reativo do tema (true = escuro). */
  readonly dark = signal<boolean>(this.readStored());

  constructor() {
    // Sincroniza o DOM com o estado inicial (o script no index.html já pode ter
    // aplicado; aqui garantimos consistência caso o storage mude entre abas).
    this.apply(this.dark());
  }

  toggle(): void {
    this.set(!this.dark());
  }

  set(dark: boolean): void {
    const commit = () => {
      this.dark.set(dark);
      this.apply(dark);
      try {
        localStorage.setItem(STORAGE_KEY, dark ? 'dark' : 'light');
      } catch {
        /* storage indisponível — ignora */
      }
    };

    const doc = document as Document & {
      startViewTransition?: (cb: () => void) => { finished?: Promise<unknown> };
    };
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Cross-fade suave (escurecendo/clareando) via View Transitions API.
    // Sem suporte ou com movimento reduzido, aplica direto.
    if (doc.startViewTransition && !reduceMotion) {
      const root = document.documentElement;
      root.classList.add('theme-anim');
      const transition = doc.startViewTransition(commit);
      Promise.resolve(transition.finished).finally(() => root.classList.remove('theme-anim'));
    } else {
      commit();
    }
  }

  private apply(dark: boolean): void {
    document.documentElement.classList.toggle('dark-mode', dark);
    const light = document.getElementById('theme-light') as HTMLLinkElement | null;
    const darkLink = document.getElementById('theme-dark') as HTMLLinkElement | null;
    if (light) light.disabled = dark;
    if (darkLink) darkLink.disabled = !dark;
  }

  private readStored(): boolean {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'dark';
    } catch {
      return false;
    }
  }
}
